import { injectable } from "inversify";
import { parse } from "sparkson";
import { VintomError } from "../../api/model/error/VintomError";
import { Config } from "../../config/config";
import { ConfigurationOverride } from "../../model/playerConfiguration/ConfigurationOverride";
import { FullPlayerConfiguration } from "../../model/playerConfiguration/FullPlayerConfiguration";
import { PlayerConfiguration } from "../../model/playerConfiguration/PlayerConfiguration";
import { PersonalizationConfig, PlayerSetupConfig } from "../../api/player/PlayerConfigTypes";

export interface ConfigurationBuilder {
  build(setup: PlayerSetupConfig): Promise<FullPlayerConfiguration>;
}

@injectable()
export class ConfigurationBuilderImpl implements ConfigurationBuilder {
  build(setupConfig: PlayerSetupConfig): Promise<FullPlayerConfiguration> {
    const { videoCode, configuration = {}, personalizationConfig = {} } = setupConfig;
    const { projectCode } = personalizationConfig;

    if (videoCode || projectCode) {
      const baseUrl = configuration.playerServerUrl || Config.defaultPlayerServerUrl;

      const playerServerUrl = Config.videoConfigurationUrl(baseUrl, projectCode ? null : videoCode);

      return this.fetchConfig(playerServerUrl, personalizationConfig).then((fetchedConfigurationJSON) =>
        this.parseAndMerge(fetchedConfigurationJSON, configuration, personalizationConfig),
      );
    } else {
      return this.parseAndMerge(configuration, {}, personalizationConfig);
    }
  }

  private parseAndMerge(setupConfig: any, overrideConfig: any, personalizationConfig: any): Promise<FullPlayerConfiguration> {
    try {
      const config = parse(PlayerConfiguration, setupConfig);
      const override = parse(ConfigurationOverride, overrideConfig);

      return Promise.resolve(
        new FullPlayerConfiguration(
          config.code,
          config.scenarioElements,
          override.analyticsEndpoints ?? config.analyticsEndpoints,
          config.aspectRatio,
          config.parts,
          config.partsDebugInfo,
          config.videoDate,
          override.playerControls ?? config.playerControls,
          config.status,
          override.playerServerUrl ?? Config.defaultPlayerServerUrl,
          override.ctaStorageUrl ?? Config.defaultCTAStorageUrl,
          this.calculateVideoJSUrl(override.jsAssetsUrl ?? config.jsAssetsUrl ?? Config.defaultJSAssetsLocation),
          this.calculateLottieUrl(override.jsAssetsUrl ?? config.jsAssetsUrl ?? Config.defaultJSAssetsLocation),
          override.adminMode ?? config.adminMode,
          override.autoplay ?? config.autoplay,
          override.mutedStart ?? config.mutedStart,
          override.subtitlesActiveOnStart ?? config.subtitlesActiveOnStart,
          personalizationConfig,
          config.campaign,
          override.skin ?? config.skin,
          config.groupId,
          config.scenarioMusicUrl,
        ),
      );
    } catch (e) {
      return Promise.reject(new VintomError("configurationParsingFailure", "Failed to parse configuration", e));
    }
  }

  private fetchConfig(serverUrl: string, personalizationConfig?: PersonalizationConfig): Promise<any> {
    const { projectCode, ...data } = personalizationConfig || {};

    const message = projectCode ? { projectCode, data } : { ...data };

    const fetchParams =
      Object.values(personalizationConfig).length === 0
        ? { method: "GET" }
        : { method: "POST", body: JSON.stringify(message), headers: { "Content-Type": "application/json" } };

    return fetch(serverUrl, fetchParams)
      .then((response) => response.json())
      .catch(() => Promise.reject(new VintomError("resourceLoadingFailure", `Failed to load configuration from '${serverUrl}'`)));
  }

  private calculateVideoJSUrl(jsAssetsUrl: string): string {
    return `${jsAssetsUrl}${Config.defaultVideoJSLocation}`;
  }

  private calculateLottieUrl(jsAssetsUrl: string): string {
    return `${jsAssetsUrl}${Config.defaultLottieJSLocation}`;
  }
}
